import { useEffect, useState } from 'react';
import { Divider } from '@mui/material';

import { Roles } from '@youscience/commonapp-service-common';

import { CoreBox, CoreTooltip, CoreTypography } from '@youscience/core';

import useRecommenderProfileStore from '@store/RecommenderProfileStore';
import useCommonAppAdminStore from '@store/CommonAppAdminStore';

import { ROUTES } from '@constants/ROUTES';
import { ECP_APPS_URL } from '@constants/externalRoutes';
import { ECP_HASH, EDC_HASH } from '@constants';

import { useRecommenderSpaceSidebar } from '@hooks/useRecommenderSpaceSidebar';

import ChevronLeftIcon from '@images/chevron-left.svg?react';
import SchoolHatIcon from '@images/school-hat.svg?react';
import FileIcon from '@images/file.svg?react';
import WarningIcon from '@images/warning.svg?react';
import CollapseIcon from '@images/Recommender/collapsed-icon.svg?react';

import {
  sxStyles,
  StyledSidebarWrap,
  StyledToggle,
  StyledSidebarList,
  StyledSidebarRow,
  StyledSidebarItem,
  StyledSectionMenu,
  StyledSectionHeader,
  StyledSectionItem,
  StyledSectionToggle,
} from './RecommenderSpaceSidebar.styles';

export const RecommenderSpaceSidebar = () => {
  const { isSidebarOpen, toggleSidebar, isCommonAppSidebarOpen, toggleCommonAppSidebar } = useRecommenderSpaceSidebar();

  const {
    formState: {
      personalDetails: personalDetailsFormState,
      certifications: certificationsFormState,
      schoolDetailsCounselor: schoolDetailsCounselorFormState,
      schoolProfile: schoolProfileFormState,
      classRank: classRankFormState,
      gpa: gpaFormState,
      transcripts: transcriptsFormState,
    },
  } = useRecommenderProfileStore((store) => store);

  const { userRoles } = useCommonAppAdminStore((store) => store);
  const isCounselorRole = userRoles?.includes(Roles.COUNSELOR);

  const [recommenderProfileChangesExist, setRecommenderProfileChangesExist] = useState(false);
  const [recommenderSchoolProfileChangesExist, setRecommenderSchoolProfileChangesExist] = useState(false);

  useEffect(() => {
    setRecommenderProfileChangesExist(
      !location.pathname.endsWith(ROUTES.RECOMMENDER_PROFILE) &&
        (personalDetailsFormState.hasChanged || certificationsFormState.hasChanged),
    );
  }, [location.pathname, personalDetailsFormState.hasChanged, certificationsFormState.hasChanged]);

  useEffect(() => {
    setRecommenderSchoolProfileChangesExist(
      !location.pathname.endsWith(ROUTES.SCHOOL_PROFILE) &&
        (schoolDetailsCounselorFormState.hasChanged ||
          schoolProfileFormState.hasChanged ||
          classRankFormState.hasChanged ||
          gpaFormState.hasChanged ||
          transcriptsFormState.hasChanged),
    );
  }, [
    location.pathname,
    schoolDetailsCounselorFormState.hasChanged,
    schoolProfileFormState.hasChanged,
    classRankFormState.hasChanged,
    gpaFormState.hasChanged,
    transcriptsFormState.hasChanged,
  ]);

  return (
    <StyledSidebarWrap data-testid="recommenderSpaceSidebar" variant="permanent" open={isSidebarOpen}>
      <CoreBox sx={sxStyles.toggleWrap}>
        <StyledToggle isSidebarOpen={isSidebarOpen} onClick={toggleSidebar}>
          <ChevronLeftIcon data-testid="sidebar-toggle-icon" />
        </StyledToggle>
      </CoreBox>

      <StyledSidebarList data-testid="sidebarList" isSidebarOpen={isSidebarOpen}>
        <CoreBox sx={sxStyles.topSidebarActions}>
          {isSidebarOpen ? (
            <CoreTypography sx={sxStyles.sidebarTitle} variant="overline" component="h6">
              Explore your tools
            </CoreTypography>
          ) : null}

          {location.hash === ECP_HASH ? (
            <StyledSidebarRow
              to={`${ECP_APPS_URL}/educationAndCareerPlan${EDC_HASH}`}
              isSelected={false}
              isSidebarOpen={isSidebarOpen}
            >
              <SchoolHatIcon />

              {isSidebarOpen ? <CoreTypography variant="body2">Education & Career Plan</CoreTypography> : null}
            </StyledSidebarRow>
          ) : null}

          <StyledSectionMenu>
            <StyledSectionHeader isSidebarOpen={isSidebarOpen}>
              <CoreBox sx={{ display: 'flex', gap: '0.75rem' }}>
                <FileIcon />

                {isSidebarOpen ? (
                  <CoreBox>
                    <CoreTypography variant="body2">Common App</CoreTypography>
                  </CoreBox>
                ) : null}
              </CoreBox>

              {isSidebarOpen ? (
                <StyledSectionToggle isSidebarOpen={isCommonAppSidebarOpen} onClick={toggleCommonAppSidebar}>
                  <ChevronLeftIcon data-testid="common-app-toggle-icon" />
                </StyledSectionToggle>
              ) : null}
            </StyledSectionHeader>

            {isCommonAppSidebarOpen ? (
              <>
                <StyledSectionItem
                  to={`${ROUTES.RECOMMENDER_SPACE}${location.hash}`}
                  isSelected={location.pathname.endsWith(ROUTES.RECOMMENDER_SPACE)}
                  isSidebarOpen={isSidebarOpen}
                >
                  {isSidebarOpen ? (
                    <CoreBox>
                      <CoreTypography variant="body2">Common App applicants</CoreTypography>
                    </CoreBox>
                  ) : (
                    <CollapseIcon />
                  )}
                </StyledSectionItem>

                <StyledSectionItem
                  to={`${ROUTES.RECOMMENDER_PROFILE}${location.hash}`}
                  isSelected={location.pathname.endsWith(ROUTES.RECOMMENDER_PROFILE)}
                  isSidebarOpen={isSidebarOpen}
                >
                  {isSidebarOpen ? (
                    <StyledSidebarItem>
                      <CoreTypography variant="body2">Recommender Profile</CoreTypography>
                      {recommenderProfileChangesExist ? <UnsavedChangesWarningIcon /> : null}
                    </StyledSidebarItem>
                  ) : (
                    <CollapseIcon />
                  )}
                </StyledSectionItem>

                {isCounselorRole ? (
                  <StyledSectionItem
                    to={`${ROUTES.SCHOOL_PROFILE}${location.hash}`}
                    isSelected={location.pathname.endsWith(ROUTES.SCHOOL_PROFILE)}
                    isSidebarOpen={isSidebarOpen}
                  >
                    {isSidebarOpen ? (
                      <StyledSidebarItem>
                        <CoreTypography variant="body2">School Profile</CoreTypography>
                        {recommenderSchoolProfileChangesExist ? <UnsavedChangesWarningIcon /> : null}
                      </StyledSidebarItem>
                    ) : (
                      <CollapseIcon />
                    )}
                  </StyledSectionItem>
                ) : null}

                <StyledSectionItem
                  to={`${ROUTES.ORGANIZATION_DASHBOARD}${location.hash}`}
                  isSelected={location.pathname.endsWith(ROUTES.ORGANIZATION_DASHBOARD)}
                  isSidebarOpen={isSidebarOpen}
                >
                  {isSidebarOpen ? (
                    <StyledSidebarItem>
                      <CoreTypography variant="body2">Organization dashboard</CoreTypography>
                    </StyledSidebarItem>
                  ) : (
                    <CollapseIcon />
                  )}
                </StyledSectionItem>
              </>
            ) : null}
          </StyledSectionMenu>
        </CoreBox>

        <Divider />
      </StyledSidebarList>
    </StyledSidebarWrap>
  );
};

const UnsavedChangesWarningIcon = () => {
  return (
    <CoreTooltip title="You have unsaved updates">
      <CoreBox sx={{ display: 'flex', alignItems: 'center' }}>
        <WarningIcon height="15" />
      </CoreBox>
    </CoreTooltip>
  );
};
